import React, { useEffect, useMemo, useState } from "react";
import { getIdTokenResult } from "firebase/auth";
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import db, { auth } from "../../Firebase";
import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";

const MAX_IN_QUERY = 10;

const mapSnapshot = (snap) =>
  snap.docs.map((snapshotDoc) => ({
    id: snapshotDoc.id,
    ...snapshotDoc.data(),
  }));

const asDate = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value?.seconds != null) return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  return null;
};

const isClaimed = (plant) => Boolean(plant?.ownedBy || plant?.owner);

const formatDate = (value) => {
  const date = asDate(value);
  return date ? date.toLocaleString() : "Unknown";
};

const fetchCollectionRows = async (collectionName, sortField, rowLimit) => {
  const rowsQuery = query(
    collection(db, collectionName),
    orderBy(sortField, "desc"),
    limit(rowLimit)
  );
  const snapshot = await getDocs(rowsQuery);
  return mapSnapshot(snapshot);
};

const fetchPlantsByIds = async (ids) => {
  const uniqueIds = Array.from(new Set((ids || []).filter(Boolean)));
  if (!uniqueIds.length) return {};

  const lookups = [];
  for (let index = 0; index < uniqueIds.length; index += MAX_IN_QUERY) {
    const chunk = uniqueIds.slice(index, index + MAX_IN_QUERY);
    lookups.push(
      getDocs(query(collection(db, "Plants"), where(documentId(), "in", chunk)))
    );
  }

  const snapshots = await Promise.all(lookups);
  return snapshots
    .flatMap((snapshot) => mapSnapshot(snapshot))
    .reduce((acc, plant) => {
      acc[plant.id] = plant;
      return acc;
    }, {});
};

const incrementMetric = (target, key, amount = 1) => {
  target[key] = (target[key] || 0) + amount;
};

const CampaignAnalytics = () => {
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [printJobs, setPrintJobs] = useState([]);
  const [scans, setScans] = useState([]);
  const [events, setEvents] = useState([]);
  const [plantsById, setPlantsById] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState("");

  useEffect(() => {
    const verifyAdmin = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setAuthError("You must be signed in to view campaign analytics.");
          setAuthReady(true);
          return;
        }

        const token = await getIdTokenResult(user);
        if (token?.claims?.admin) {
          setAuthReady(true);
          return;
        }

        const userDoc = await getDoc(doc(db, "Users", user.uid));
        if (userDoc.exists() && userDoc.data()?.isAdmin) {
          setAuthReady(true);
          return;
        }

        setAuthError("Admin access required.");
      } catch (err) {
        console.error("Admin check failed", err);
        setAuthError("Unable to verify admin access.");
      } finally {
        setAuthReady(true);
      }
    };

    verifyAdmin();
  }, []);

  useEffect(() => {
    if (!authReady || authError) return;

    const loadData = async () => {
      setLoading(true);
      setError("");

      try {
        const [printJobRows, scanRows, eventRows] = await Promise.all([
          fetchCollectionRows("PrintJobs", "createdAt", 200),
          fetchCollectionRows("ScanTracking", "ts", 5000),
          fetchCollectionRows("CampaignEvents", "ts", 5000),
        ]);

        const plantIds = printJobRows.flatMap((job) =>
          (job.generatedPlants || []).map((item) => item.plantID).filter(Boolean)
        );
        const plantLookup = await fetchPlantsByIds(plantIds);

        setPrintJobs(printJobRows);
        setScans(scanRows);
        setEvents(eventRows);
        setPlantsById(plantLookup);
      } catch (err) {
        console.error("Failed to load campaign analytics", err);
        setError(err.message || "Failed to load campaign analytics.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [authReady, authError]);

  const campaigns = useMemo(() => {
    return Array.from(
      new Set(
        [...printJobs, ...scans, ...events]
          .map((item) => item?.campaign)
          .filter(Boolean)
      )
    ).sort((left, right) => left.localeCompare(right));
  }, [printJobs, scans, events]);

  useEffect(() => {
    if (!campaigns.length) return;
    if (!selectedCampaign || !campaigns.includes(selectedCampaign)) {
      setSelectedCampaign(campaigns[0]);
    }
  }, [campaigns, selectedCampaign]);

  const campaignView = useMemo(() => {
    if (!selectedCampaign) {
      return {
        jobs: [],
        scansForCampaign: [],
        eventsForCampaign: [],
        stats: {
          totalTags: 0,
          totalSheets: 0,
          totalScans: 0,
          uniqueScannedPlants: 0,
          claimedPlants: 0,
          claimRateFromPrinted: 0,
          claimRateFromScanned: 0,
        },
        speciesRows: [],
        jobRows: [],
        eventRows: [],
      };
    }

    const jobs = printJobs.filter((job) => job.campaign === selectedCampaign);
    const scansForCampaign = scans.filter((scan) => scan.campaign === selectedCampaign);
    const eventsForCampaign = events.filter((event) => event.campaign === selectedCampaign);
    const printedItems = jobs.flatMap((job) => job.generatedPlants || []);
    const printedPlantIds = Array.from(
      new Set(printedItems.map((item) => item.plantID).filter(Boolean))
    );
    const scannedPlantIds = Array.from(
      new Set(scansForCampaign.map((scan) => scan.plantID).filter(Boolean))
    );
    const claimedPlantIds = printedPlantIds.filter((plantId) => isClaimed(plantsById[plantId]));
    const claimedScannedPlantIds = scannedPlantIds.filter((plantId) =>
      isClaimed(plantsById[plantId])
    );

    const itemByPlantId = printedItems.reduce((acc, item) => {
      if (item?.plantID && !acc[item.plantID]) {
        acc[item.plantID] = item;
      }
      return acc;
    }, {});

    const speciesMetrics = {};
    printedItems.forEach((item) => {
      const key = item.plantType || item.species || "Unknown species";
      if (!speciesMetrics[key]) {
        speciesMetrics[key] = {
          species: key,
          printed: 0,
          totalScans: 0,
          uniqueScannedPlants: 0,
          claimedPlants: 0,
          scannedPlantIds: new Set(),
        };
      }
      speciesMetrics[key].printed += 1;
    });

    scansForCampaign.forEach((scan) => {
      const fallbackSpecies =
        itemByPlantId[scan.plantID]?.plantType ||
        itemByPlantId[scan.plantID]?.species ||
        scan.species ||
        plantsById[scan.plantID]?.species ||
        "Unknown species";
      if (!speciesMetrics[fallbackSpecies]) {
        speciesMetrics[fallbackSpecies] = {
          species: fallbackSpecies,
          printed: 0,
          totalScans: 0,
          uniqueScannedPlants: 0,
          claimedPlants: 0,
          scannedPlantIds: new Set(),
        };
      }
      speciesMetrics[fallbackSpecies].totalScans += 1;
      if (scan.plantID) {
        speciesMetrics[fallbackSpecies].scannedPlantIds.add(scan.plantID);
      }
    });

    claimedPlantIds.forEach((plantId) => {
      const speciesName =
        itemByPlantId[plantId]?.plantType ||
        itemByPlantId[plantId]?.species ||
        plantsById[plantId]?.species ||
        "Unknown species";
      if (!speciesMetrics[speciesName]) {
        speciesMetrics[speciesName] = {
          species: speciesName,
          printed: 0,
          totalScans: 0,
          uniqueScannedPlants: 0,
          claimedPlants: 0,
          scannedPlantIds: new Set(),
        };
      }
      speciesMetrics[speciesName].claimedPlants += 1;
    });

    const speciesRows = Object.values(speciesMetrics)
      .map((row) => ({
        species: row.species,
        printed: row.printed,
        totalScans: row.totalScans,
        uniqueScannedPlants: row.scannedPlantIds.size,
        claimedPlants: row.claimedPlants,
      }))
      .sort((left, right) => right.totalScans - left.totalScans || right.printed - left.printed);

    const jobRows = jobs.map((job) => {
      const plantIds = new Set(
        (job.generatedPlants || []).map((item) => item.plantID).filter(Boolean)
      );
      const jobScans = scansForCampaign.filter(
        (scan) => scan.printJobId === job.id || (scan.plantID && plantIds.has(scan.plantID))
      );
      const uniqueScanned = new Set(jobScans.map((scan) => scan.plantID).filter(Boolean));
      const claimed = Array.from(plantIds).filter((plantId) => isClaimed(plantsById[plantId]));

      return {
        id: job.id,
        createdAt: job.createdAt,
        totalTags: job.totalCount || (job.generatedPlants || []).length,
        sheetCount: job.sheetCount || 0,
        scans: jobScans.length,
        uniqueScanned: uniqueScanned.size,
        claimed: claimed.length,
        speciesMix: (job.requestedItems || [])
          .map((item) => `${item.species} x ${item.count}`)
          .join(", "),
      };
    });

    const eventCounts = {};
    eventsForCampaign.forEach((event) => {
      incrementMetric(eventCounts, event.eventType || "unknown");
    });

    return {
      jobs,
      scansForCampaign,
      eventsForCampaign,
      stats: {
        totalTags: printedItems.length,
        totalSheets: jobs.reduce((sum, job) => sum + (job.sheetCount || 0), 0),
        totalScans: scansForCampaign.length,
        uniqueScannedPlants: scannedPlantIds.length,
        claimedPlants: claimedPlantIds.length,
        claimRateFromPrinted: printedItems.length
          ? Math.round((claimedPlantIds.length / printedItems.length) * 100)
          : 0,
        claimRateFromScanned: scannedPlantIds.length
          ? Math.round((claimedScannedPlantIds.length / scannedPlantIds.length) * 100)
          : 0,
      },
      speciesRows,
      jobRows,
      eventRows: Object.entries(eventCounts)
        .map(([eventType, count]) => ({ eventType, count }))
        .sort((left, right) => right.count - left.count),
    };
  }, [events, plantsById, printJobs, scans, selectedCampaign]);

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f4efe8" }}>
      <ConsoleHomePageHeader />
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "20px" }}>
        <h2 style={{ marginTop: 0 }}>Campaign Analytics</h2>
        <p style={{ maxWidth: "760px" }}>
          Scans come from campaign QR visits. Claimed plants are inferred from printed plants
          that now have an owner in the app. If the app later calls
          <code> /trackCampaignEvent</code>, those event counts will appear below too.
        </p>

        {authError ? <div style={{ color: "#8b1e1e" }}>{authError}</div> : null}
        {error ? <div style={{ color: "#8b1e1e" }}>{error}</div> : null}
        {loading ? <div>Loading campaign analytics...</div> : null}

        {!loading && campaigns.length ? (
          <>
            <div style={{ marginBottom: 20 }}>
              <label htmlFor="campaignSelect" style={{ fontWeight: 600 }}>
                Campaign
              </label>
              <select
                id="campaignSelect"
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                style={{ marginLeft: 12, padding: "8px 10px", minWidth: 240 }}
              >
                {campaigns.map((campaign) => (
                  <option key={campaign} value={campaign}>
                    {campaign}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                gap: 12,
                marginBottom: 24,
              }}
            >
              {[
                ["Printed tags", campaignView.stats.totalTags],
                ["Sheets", campaignView.stats.totalSheets],
                ["Total scans", campaignView.stats.totalScans],
                ["Unique scanned plants", campaignView.stats.uniqueScannedPlants],
                ["Claimed plants", campaignView.stats.claimedPlants],
                ["Claim rate from printed", `${campaignView.stats.claimRateFromPrinted}%`],
                ["Claim rate from scanned", `${campaignView.stats.claimRateFromScanned}%`],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    backgroundColor: "white",
                    borderRadius: 10,
                    padding: 16,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  }}
                >
                  <div style={{ fontSize: 13, color: "#6b5b4b" }}>{label}</div>
                  <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}>{value}</div>
                </div>
              ))}
            </div>

            <section style={{ marginBottom: 24 }}>
              <h3>Species Performance</h3>
              <div style={{ overflowX: "auto", backgroundColor: "white", borderRadius: 10 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {[
                        "Species",
                        "Printed",
                        "Total scans",
                        "Unique scanned",
                        "Claimed",
                      ].map((heading) => (
                        <th
                          key={heading}
                          style={{ textAlign: "left", padding: 12, borderBottom: "1px solid #e6dccf" }}
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {campaignView.speciesRows.map((row) => (
                      <tr key={row.species}>
                        <td style={{ padding: 12, borderBottom: "1px solid #f1e8dc" }}>{row.species}</td>
                        <td style={{ padding: 12, borderBottom: "1px solid #f1e8dc" }}>{row.printed}</td>
                        <td style={{ padding: 12, borderBottom: "1px solid #f1e8dc" }}>{row.totalScans}</td>
                        <td style={{ padding: 12, borderBottom: "1px solid #f1e8dc" }}>
                          {row.uniqueScannedPlants}
                        </td>
                        <td style={{ padding: 12, borderBottom: "1px solid #f1e8dc" }}>{row.claimedPlants}</td>
                      </tr>
                    ))}
                    {!campaignView.speciesRows.length ? (
                      <tr>
                        <td colSpan={5} style={{ padding: 12 }}>
                          No species data yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>

            <section style={{ marginBottom: 24 }}>
              <h3>Print Job Progress</h3>
              <div style={{ display: "grid", gap: 12 }}>
                {campaignView.jobRows.map((job) => (
                  <div
                    key={job.id}
                    style={{
                      backgroundColor: "white",
                      borderRadius: 10,
                      padding: 16,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>
                      Print Job <code>{job.id}</code>
                    </div>
                    <div style={{ marginTop: 6 }}>Created: {formatDate(job.createdAt)}</div>
                    <div style={{ marginTop: 6 }}>
                      Tags: {job.totalTags} · Sheets: {job.sheetCount} · Scans: {job.scans} ·
                      Unique scanned: {job.uniqueScanned} · Claimed: {job.claimed}
                    </div>
                    <div style={{ marginTop: 6, color: "#6b5b4b" }}>{job.speciesMix}</div>
                  </div>
                ))}
                {!campaignView.jobRows.length ? (
                  <div style={{ backgroundColor: "white", borderRadius: 10, padding: 16 }}>
                    No print jobs recorded for this campaign yet.
                  </div>
                ) : null}
              </div>
            </section>

            <section>
              <h3>Campaign Events</h3>
              <div style={{ backgroundColor: "white", borderRadius: 10, padding: 16 }}>
                {campaignView.eventRows.length ? (
                  campaignView.eventRows.map((row) => (
                    <div key={row.eventType} style={{ marginBottom: 8 }}>
                      <strong>{row.eventType}</strong>: {row.count}
                    </div>
                  ))
                ) : (
                  <div>No extra campaign events recorded yet.</div>
                )}
              </div>
            </section>
          </>
        ) : null}

        {!loading && !campaigns.length && !authError ? (
          <div>No campaigns found yet.</div>
        ) : null}
      </div>
    </div>
  );
};

export default CampaignAnalytics;
